import { useFormContext } from 'react-hook-form';
import { Button, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { Form } from '@ui';
import { toast } from 'react-toastify';
import { DeclineCaseForm } from '@lp/features/forms/DeclineCaseForm';
import { DeclineCaseFormParams, declineCaseSchema } from '../config';
import { CancelationReasons, defaultValues, Specifications } from './config';
import { useDeclineCase } from '@/queries/case';
import { retrieveUnifiedError } from '@/api/utils';
import { Uuid } from '@/api/types';
import { CaseStatus } from '@/api/apis/case';

const SubmitButton = ({ isPending }: { isPending: boolean }) => {
  const {
    formState: { isValid },
  } = useFormContext<DeclineCaseFormParams>();
  const isDisabled = isPending || !isValid;

  return (
    <Button size="md" type="submit" isLoading={isPending} isDisabled={isDisabled} color="danger">
      Update Status
    </Button>
  );
};

export const DeclineCaseModal = ({
  onOpenChange,
  caseId,
  status,
}: {
  onOpenChange: () => void;
  caseId: Uuid;
  status: CaseStatus;
}) => {
  const { mutateAsync, isPending } = useDeclineCase();

  const handleDeclineCase = async (data: DeclineCaseFormParams) => {
    try {
      const { dispositionComment } = data;
      await mutateAsync({ caseId, dispositionComment });
      onOpenChange();
      toast.success('Case has been declined');
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  return (
    <Modal
      isOpen
      onOpenChange={onOpenChange}
      size="3xl"
      classNames={{
        wrapper: 'flex-wrap z-[420]',
        footer: 'flex flex-col gap-4',
        closeButton: 'text-hl',
        backdrop: 'z-[419]',
      }}
    >
      <ModalContent>
        <Form defaultValues={defaultValues} schema={declineCaseSchema} onSubmit={handleDeclineCase}>
          <ModalHeader className="flex flex-col gap-2">
            <h3 className="text-hl">Case Declined</h3>
            <div className="text-l-r text-base-20">
              The case will be canceled across all system. This action cannot be undone
            </div>
            <Divider />
          </ModalHeader>
          <ModalBody className="py-6">
            <DeclineCaseForm
              status={status}
              dispositionReasons={CancelationReasons}
              dispositionSpecifications={Specifications}
              disabledOutcomes
            />
          </ModalBody>
          <ModalFooter>
            <Divider />
            <div className="flex gap-2 justify-end">
              <Button size="md" isDisabled={isPending} variant="outline" onClick={onOpenChange}>
                Discard
              </Button>
              <SubmitButton isPending={isPending} />
            </div>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
